import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, FormControl, InputLabel, Paper, TextField } from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import Book from '@mui/icons-material/Book';
import CustomTable from '../tables/MuiDataGrid';
import DateRange from '../dateRange';
import { formatDateTime } from '../../helper/formatDate';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Action Logs';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    actionLogs: [],
  });
  const [filterOptions, setFilterOptions] = React.useState({
    users: [],
    area: [],
    type: [],
  });

  //form input state
  const [searchInput, setSearchInput] = React.useState({
    user: 'All',
    type: 'All',
    area: 'All',
    from_date: '',
    to_date: '',
  });

  // Effect function: executed when perPage or page changes
  // React.useEffect(() => {
  //     setDataIsFetching(false);
  // }, [perPage, page]);
  // Dependency array: re-run effect when perPage or page changes

  React.useEffect(() => {
    setDataIsFetching((current) => {
      if (!current) return true;
    });
    userRequest()
      .get(`/action-logs?per_page=${perPage}&page=${page}&${buildQuery()}`)
      .then(({ data }) => {
        console.log(data);
        // Update the tableData state with the retrieved exceptions
        setTableData({
          count: data.total,
          actionLogs: data.actionLogs,
        });
        setDataIsFetching(false);
      })
      // Log any errors that occur during the request
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }, [perPage, page, searchInput]);
  // Dependency array: re-run effect when searchInput.area or searchInput.type changes

  // Effect function: executed once when the component mounts
  React.useEffect(() => {
    bulkAxios(['/users/names', '/action-logs/areas', '/action-logs/types'])
      .then((all) => {
        // Once all requests are resolved, update the filter options state
        setFilterOptions({
          users: all[0].users,
          area: all[1].actionLogAreas.sort((a, b) => a.display_name - b.display_name),
          type: all[2].actionLogTypes.sort((a, b) => a.display_name - b.display_name),
          columns: null,
        });
        // Log the response data from all requests
        console.log(all);
      })
      .catch((error) => console.log(error)); // Log any errors that occur during the requests
  }, []);

  /**
   * Performs bulk requests using Axios library.
   *
   * @param {Array} urls - Array of URLs to request.
   * @returns {Promise<Array>} - A Promise that resolves to an array of response data for each URL.
   */
  function bulkAxios(urls = null) {
    // Check if urls parameter is provided
    if (!urls) return urls;

    // Create an array of promises for each URL request
    let promises = urls.map((req) =>
      userRequest() // userRequest is a function returning an Axios instance (requestMethods.js)
        .get(req) // Send a GET request to the URL
        .then(({ data }) => {
          // Extract response data from the Axios response
          return data;
        })
        .catch((error) => {
          // Log any errors that occur during the request
          console.log(error);
          return null;
        })
    );
    // Wait for all promises to resolve and return the result as a Promise
    return Promise.all(promises);
  }

  /**
   * Retrieves column names for a specific table from the server.
   *
   * @param {string} table - The name of the table to retrieve column names from.
   * @returns {Promise<Array|null>} - A Promise that resolves to an array of column names, or null if an error occurs.
   */
  async function getColumnNames(table) {
    try {
      // Send a GET request to retrieve column names for the specified table
      const { data } = await userRequest().get(`/action-logs/table-columns/${adjustSrting(table)}`);

      // Update the filterOptions state with the retrieved column names
      setFilterOptions((pr) => ({
        ...pr,
        columns: data.columns.map((c) => adjustSrting(c.COLUMN_NAME, false)),
      }));

      // Return an array of column names after adjusting their strings
      return data.columns.map((c) => adjustSrting(c.COLUMN_NAME, false));
    } catch (error) {
      // Log any errors that occur during the request and return null
      return null;
    }
  }

  const columns = [
    {
      field: 'start_date_time',
      headerName: 'Date/Time',
      flex: 2,
      valueGetter: ({ row }) => formatDateTime(new Date(row.start_date_time)),
    },
    {
      field: 'userName',
      headerName: 'User',
      flex: 1,
    },
    {
      field: 'user_ip_address',
      headerName: 'IP',
      flex: 1,
    },
    {
      field: 'alArea',
      headerName: 'Area',
      flex: 1,
    },
    {
      field: 'alType',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'context',
      headerName: 'Context',
      flex: 1,
    },
    {
      field: 'table_name',
      headerName: 'Table',
      flex: 1,
    },
    {
      field: 'before',
      headerName: 'Before',
      flex: 1,
    },
    {
      field: 'after',
      headerName: 'After',
      flex: 1,
    },
  ];

  /**
   * Handles the input change event and updates the search input state.
   *
   * @param {object} event - The input change event object.
   */
  const onChangeInput = (e, val, name = null) => {
    if (name) setSearchInput({ ...searchInput, [name]: val });
    else setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
  };

  const onDateChange = (element) => {
    console.table(element);
    setSearchInput((prevSearchInput) => {
      switch (element.field) {
        case 'to':
          const updatedToSearchInput = {
            ...prevSearchInput,
            to_date: element.value,
          };
          return updatedToSearchInput;
        case 'from':
          const updatedFromSearchInput = {
            ...prevSearchInput,
            from_date: element.value,
          };
          return updatedFromSearchInput;
        default:
          console.error('This point should not have been hit', element);
          return prevSearchInput;
      }
    });
  };

  /**
   * Used for the chips in the filter accordion
   */
  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'user') {
            if (value === 'All') {
              return [];
            } else if (value === 'System') {
              return [];
            } else {
              return `User:${filterOptions.users.find((x) => x.id === value)?.user}`;
            }
          } else if (key === 'area') {
            if (value === 'All') {
              return [];
            } else {
              return `Area:${filterOptions.area.find((x) => x.id === value)?.display_name}`;
            }
          } else if (key === 'type') {
            if (value === 'All') {
              return [];
            } else {
              return `Type:${filterOptions.type.find((x) => x.id === value)?.display_name}`;
            }
          } else if (key === 'from_date') {
            return `From Date:${!isNaN(Date.parse(value)) ? dateToFormattedString(new Date(value)) : ''}`;
          } else if (key === 'to_date') {
            return `To Date:${!isNaN(Date.parse(value)) ? dateToFormattedString(new Date(value)) : ''}`;
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);
  }

  // function getFiltersss() {
  //     return Object.entries(searchInput)
  //         .map(([key, value]) => {
  //             if (value !== "") {
  //                 switch (key) {
  //                     case "user":
  //                         return `User: ${value === null ? "System" : filterOptions.users.find((x) => x.id === value)?.user}`;
  //                     case "area":
  //                         return `Area:${filterOptions.area.find((x) => x.id === value)?.display_name}`;
  //                     case "type":
  //                         return `Type:${filterOptions.type.find((x) => x.id === value)?.display_name}`;
  //                     case "from_date":
  //                         return `From Date:${!isNaN(Date.parse(value)) ? dateToFormattedString(new Date(value)) : ""}`;
  //                     case "to_date":
  //                         return `To Date:${!isNaN(Date.parse(value)) ? dateToFormattedString(new Date(value)) : ""}`;
  //                     default:
  //                 }
  //             }
  //             return undefined
  //         })
  //         .filter((x) => x !== undefined);
  // }

  /**
   * Handles form submission for searching exceptions.
   *
   * @param {Event} e - The form submit event.
   */
  function buildQuery(foundFilter = null) {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        let tempKey = key;
        let tempValue = value;

        if (value !== '') {
          if (foundFilter) foundFilter.push(key);

          if (key === 'is_active') {
            if (value === 'ALL') {
              tempKey = '';
              tempValue = '';
            } else if (value === 'ACTIVE') {
              tempValue = true;
            } else if (value === 'INACTIVE') {
              tempValue = false;
            }
          } else if (key === 'user' || key === 'area' || key === 'type') {
            if (value === 'All') {
              tempKey = '';
              tempValue = '';
            }
          }

          if (key === 'area' && value !== 'All') {
            tempKey = 'action_log_area_id';
            tempValue = Number(value);
          }
          if (key === 'type' && value !== 'All') {
            tempKey = 'action_log_type_id';
            tempValue = Number(value);
          }

          // Check for "All" and treat it like an empty string
          if (tempValue === 'All') {
            tempKey = '';
            tempValue = '';
          }

          // Only return if the tempValue is not "All"
          if (tempValue !== 'All') {
            return encodeURIComponent(tempKey) + '=' + encodeURIComponent(tempValue);
          }
        }

        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }
  const onSearch = (e) => {
    e.preventDefault();
    const queryString = buildQuery();
    // Generate the query string based on the search input

    // Send a GET request to retrieve exceptions based on the query string
    userRequest()
      .get(`/action-logs?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        // Update the tableData state with the retrieved exceptions
        setTableData({
          count: data.total,
          actionLogs: data.actionLogs,
        });
      })
      // Log any errors that occur during the request
      .catch(({ response }) => {
        console.log(response);
        setTableData({ count: 0, actionLogs: [] });
      });
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Book />} title="Action Logs" parent="Action logs" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              {/* User */}
              <FormControl>
                <InputLabel>User</InputLabel>
                <Select
                  name="user"
                  value={searchInput.user}
                  onChange={onChangeInput}
                  label="User"
                  style={{
                    fontStyle: searchInput.user === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={'System'}>System</MenuItem>
                  {filterOptions.users.map((val, i) => (
                    <MenuItem key={i} value={val.id}>
                      {val.user} - <i>{val.email}</i>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              {/* Area */}
              <FormControl>
                <InputLabel>Area</InputLabel>
                <Select
                  name="area"
                  value={searchInput.area}
                  onChange={onChangeInput}
                  label="Area"
                  style={{
                    fontStyle: searchInput.area === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.area.map((val, i) => (
                    <MenuItem key={i} value={val.id}>
                      {val.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              {/* Type */}
              <FormControl>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={searchInput.type}
                  onChange={onChangeInput}
                  label="Type"
                  style={{
                    fontStyle: searchInput.type === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.type.map((val, i) => (
                    <MenuItem key={i} value={val.id}>
                      {val.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              {/* Date Range */}
              <DateRange
                from={searchInput.from_date}
                to={searchInput.to_date}
                toOnChange={
                  (val) => {
                    onDateChange({ field: 'to', value: val });
                  } /* onChangeInput({ target: { name: "to_date", value: val } }) */
                }
                fromOnChange={
                  (val) => {
                    onDateChange({ field: 'from', value: val });
                  } /* onChangeInput({ target: { name: "from_date", value: val } }) */
                }
                label="Date Range"
              />
            </div>

            {!!filterOptions.columns && (
              <>
                <Paper elevation={2} className="form-group row mx-0 p-3">
                  <h5>Before Parameters</h5>
                  {filterOptions.columns.map((val, i) => (
                    <TextField
                      key={i}
                      className="row mx-0 my-2"
                      label={val}
                      // name="to"
                      // onChange={onChangeInput}
                      variant="outlined"
                    />
                  ))}
                </Paper>
                <Paper elevation={2} className="form-group row mx-0 p-2">
                  <h5>After Parameters</h5>
                  {filterOptions.columns.map((val, i) => (
                    <TextField
                      key={i}
                      className="row mx-0 my-2"
                      label={val}
                      // name="to"
                      // onChange={onChangeInput}
                      variant="outlined"
                    />
                  ))}
                </Paper>
              </>
            )}
          </form>
        </FilterAccordian>
      </div>
      <br />
      <br />
      <div className="container-fluid">
        <CustomTable
          rows={tableData.actionLogs}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'start_date_time', sort: 'asc' }]}
          getRowId={(row) => row.row_id}
        />
      </div>
    </Fragment>
  );
};

function dateToFormattedString(d) {
  var yyyy = d.getFullYear().toString();
  var mm = (d.getMonth() + 1).toString(); // getMonth() is zero-based
  var dd = d.getDate().toString();

  return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
}

function adjustSrting(str = '', unformat = true) {
  if (unformat) return str.replaceAll(' ', '_').toLowerCase();
  else
    return str.replaceAll('_', ' ').replace(/(?:^|\s)\S/g, function (a) {
      return a.toUpperCase();
    });
}
export default Manage;
